import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import {
  Brain,
  Plus,
  Trash2,
  Save,
  Play,
  MessageSquare,
  BarChart3,
  TestTube,
  BookOpen,
  RefreshCw,
  CheckCircle,
  Upload,
  FileText,
  Search,
  ToggleLeft,
  ToggleRight,
  Edit,
  Eye,
  X
} from 'lucide-react';

const ChatbotAITrainer = ({ chatbot, languages, onSave }) => {
  const [activeTab, setActiveTab] = useState('training');
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);

  // Fetch analytics data
  const { data: analyticsResponse, isLoading: analyticsLoading } = useQuery(
    ['chatbot-analytics', chatbot?.id],
    () => apiClient.get(`/api/chatbot/${chatbot?.id}/analytics`),
    {
      enabled: !!chatbot?.id && activeTab === 'analytics',
      refetchOnWindowFocus: false
    }
  );

  const analyticsData = analyticsResponse?.data?.analytics;

  // Parse AI config
  const aiConfig = chatbot?.ai_config ? (
    typeof chatbot.ai_config === 'string' ? JSON.parse(chatbot.ai_config) : chatbot.ai_config
  ) : {
    provider: 'openai',
    model: 'gpt-3.5-turbo',
    api_key: '',
    system_prompt: 'You are a helpful customer service assistant.',
    context_window: 5,
    learning_enabled: true
  };

  // Test AI configuration mutation
  const testMutation = useMutation(
    (data) => apiClient.post('/api/chatbot/test-ai', data),
    {
      onSuccess: (response) => {
        setTestResult(response.data);
        toast.success('AI test completed');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'AI test failed');
      }
    }
  );

  const handleSave = () => {
    const chatbotData = {
      ai_config: aiConfig,
      status: chatbot.status
    };
    onSave(chatbotData);
  };



  const testAI = () => {
    if (!testMessage.trim()) {
      toast.error('Please enter a test message');
      return;
    }

    testMutation.mutate({
      message_text: testMessage,
      conversation_context: []
    });
  };

  const tabs = [
    { id: 'training', name: 'Training Data', icon: BookOpen },
    { id: 'testing', name: 'Test AI', icon: TestTube },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">AI Chatbot Trainer</h3>
          <p className="text-sm text-gray-600">
            Train and configure your AI-powered chatbot with training data (text and FAQs).
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleSave}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Save className="mr-2 h-4 w-4" />
            Save Changes
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Training Data Tab */}
          {activeTab === 'training' && (
            <TrainingDataTab chatbot={chatbot} />
          )}

          {/* Test AI Tab */}
          {activeTab === 'testing' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">Test AI Chatbot</h4>
                <p className="text-sm text-gray-600">
                  Test how your AI chatbot will respond to different messages using your training data.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test Message
                    </label>
                    <textarea
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      placeholder="Enter a message to test how the AI will classify it..."
                    />
                  </div>

                  <button
                    onClick={testAI}
                    disabled={testMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {testMutation.isLoading ? (
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="mr-2 h-4 w-4" />
                    )}
                    Test AI Response
                  </button>
                </div>

                {testResult && (
                  <div className="mt-6">
                    {/* Response Preview – how the chatbot reply will look in chat */}
                    {(testResult.parsed?.answer || testResult.raw_response) && (
                      <div className="rounded-lg border border-gray-200 bg-[#f5f0e8] p-4">
                        <p className="text-xs font-medium text-gray-500 mb-2">Response Preview</p>
                        <div className="flex justify-start">
                          <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-white/95 shadow-sm border border-gray-200/80 px-4 py-3">
                            <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                              {testResult.parsed?.answer || testResult.raw_response}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">AI Performance Analytics</h4>
                <p className="text-sm text-gray-600">
                  Monitor your AI chatbot's performance and accuracy over time.
                </p>
              </div>

              {analyticsLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-500">Loading analytics...</p>
                </div>
              ) : analyticsData ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <CheckCircle className="h-8 w-8 text-blue-600" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-blue-600">Accuracy Rate</p>
                          <p className="text-2xl font-bold text-blue-900">
                            {(parseFloat(analyticsData.average_confidence) * 100).toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <MessageSquare className="h-8 w-8 text-green-600" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-green-600">Conversations</p>
                          <p className="text-2xl font-bold text-green-900">
                            {analyticsData.unique_conversations || 0}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <Brain className="h-8 w-8 text-purple-600" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-purple-600">AI Responses</p>
                          <p className="text-2xl font-bold text-purple-900">
                            {analyticsData.total_responses || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Token Usage Card */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h5 className="text-sm font-medium text-gray-700 mb-4">Total Tokens Used</h5>
                      <p className="text-3xl font-bold text-gray-900">
                        {analyticsData.total_tokens ? parseInt(analyticsData.total_tokens).toLocaleString() : 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Estimated based on prompt and response length
                      </p>
                    </div>
                  </div>

                  {/* Additional Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h5 className="text-sm font-medium text-gray-700 mb-4">Average Response Time</h5>
                      <p className="text-3xl font-bold text-gray-900">
                        {analyticsData.average_response_time_ms ? `${parseInt(analyticsData.average_response_time_ms)}ms` : 'N/A'}
                      </p>
                    </div>

                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h5 className="text-sm font-medium text-gray-700 mb-4">Training Data Usage</h5>
                      <div className="space-y-2">
                        {analyticsData.training_data_usage && analyticsData.training_data_usage.length > 0 ? (
                          analyticsData.training_data_usage.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 capitalize">{item.type}</span>
                              <span className="text-sm font-medium text-gray-900">{item.count} uses</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500">No training data used yet</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Daily Usage Chart */}
                  {analyticsData.daily_usage && analyticsData.daily_usage.length > 0 && (
                    <div className="bg-white p-6 rounded-lg border border-gray-200 mt-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-4">Daily Usage (Last 30 Days)</h5>
                      <div className="flex items-end space-x-1 h-32">
                        {analyticsData.daily_usage.slice().reverse().map((day, idx) => {
                          const maxCount = Math.max(...analyticsData.daily_usage.map(d => d.count));
                          const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center">
                              <div
                                className="w-full bg-blue-600 rounded-t"
                                style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                                title={`${day.date}: ${day.count} responses`}
                              ></div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Analytics Data Yet</h3>
                  <p className="text-gray-500">
                    Analytics will appear here once your AI chatbot starts processing messages.
                  </p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

    </div>
  );
};

// Training Data Tab Component
const TrainingDataTab = ({ chatbot }) => {
  const [activeSection, setActiveSection] = useState('text');
  const [texts, setTexts] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [websites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [showViewFAQModal, setShowViewFAQModal] = useState(false);
  const [showViewWebsiteModal, setShowViewWebsiteModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [viewingFAQ, setViewingFAQ] = useState(null);
  const [viewingWebsite, setViewingWebsite] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [textContent, setTextContent] = useState('');
  const [faqData, setFaqData] = useState({ question: '', answer: '' });
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [pageContent, setPageContent] = useState('');

  const chatbotId = chatbot?.id;

  // Fetch training data
  const fetchTrainingData = async () => {
    if (!chatbotId) return;

    setLoading(true);
    try {
      // Website training data is currently disabled
      const [textsRes, faqsRes] = await Promise.all([
        apiClient.get(`/api/chatbot/${chatbotId}/training/text`),
        apiClient.get(`/api/chatbot/${chatbotId}/training/faqs`)
        // apiClient.get(`/api/chatbot/${chatbotId}/training/websites`) // Disabled
      ]);

      setTexts(textsRes.data.texts || []);
      setFaqs(faqsRes.data.faqs || []);
      // setWebsites(websitesRes.data.websites || []); // Disabled
    } catch (error) {
      console.error('Error fetching training data:', error);
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (chatbotId) {
      fetchTrainingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotId]);

  // Auto-refresh when websites are processing
  useEffect(() => {
    if (!chatbotId) return;

    const hasProcessing = websites.some(w => w.status === 'processing');
    if (!hasProcessing) return;

    const interval = setInterval(() => {
      fetchTrainingData();
    }, 2000); // Refresh every 2 seconds when processing

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websites, chatbotId]);

  // Text section handlers
  const handleAddText = async () => {
    if (!textContent.trim()) {
      toast.error('Please enter text content');
      return;
    }

    try {
      await apiClient.post(`/api/chatbot/${chatbotId}/training/text`, {
        content: textContent,
        is_enabled: true
      });
      toast.success('Text added successfully');
      setTextContent('');
      setShowTextModal(false);
      fetchTrainingData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add text');
    }
  };

  const handleDeleteText = async (id) => {
    if (!window.confirm('Are you sure you want to delete this text?')) return;

    try {
      await apiClient.delete(`/api/chatbot/${chatbotId}/training/text/${id}`);
      toast.success('Text deleted successfully');
      fetchTrainingData();
    } catch (error) {
      toast.error('Failed to delete text');
    }
  };

  const handleToggleText = async (id, isEnabled) => {
    try {
      await apiClient.put(`/api/chatbot/${chatbotId}/training/text/${id}`, {
        is_enabled: !isEnabled
      });
      fetchTrainingData();
    } catch (error) {
      toast.error('Failed to update text');
    }
  };

  // FAQ section handlers
  const handleAddFAQ = async () => {
    if (!faqData.question.trim() || !faqData.answer.trim()) {
      toast.error('Please enter both question and answer');
      return;
    }

    try {
      if (editingFAQ) {
        // Update existing FAQ
        await apiClient.put(`/api/chatbot/${chatbotId}/training/faqs/${editingFAQ.id}`, faqData);
        toast.success('FAQ updated successfully');
      } else {
        // Add new FAQ
        await apiClient.post(`/api/chatbot/${chatbotId}/training/faqs`, faqData);
        toast.success('FAQ added successfully');
      }
      setFaqData({ question: '', answer: '' });
      setEditingFAQ(null);
      setShowFAQModal(false);
      fetchTrainingData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save FAQ');
    }
  };

  const handleEditFAQ = (faq) => {
    setEditingFAQ(faq);
    setFaqData({ question: faq.question, answer: faq.answer });
    setShowFAQModal(true);
  };

  const handleViewFAQ = (faq) => {
    setViewingFAQ(faq);
    setShowViewFAQModal(true);
  };

  const handleUpdateWebsite = async () => {
    if (!websiteUrl.trim()) {
      toast.error('Please enter a website URL');
      return;
    }

    try {
      const updateData = {
        website_url: websiteUrl
      };

      // Include page content if it was edited
      if (pageContent !== undefined && pageContent !== viewingWebsite.scraped_content) {
        updateData.scraped_content = pageContent;
      }

      await apiClient.put(`/api/chatbot/${chatbotId}/training/websites/${viewingWebsite.id}`, updateData);
      toast.success('Website updated successfully');
      setWebsiteUrl('');
      setPageContent('');
      setViewingWebsite(null);
      setShowWebsiteModal(false);
      setShowViewWebsiteModal(false);
      fetchTrainingData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update website');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleViewWebsite = (website) => {
    setViewingWebsite(website);
    setWebsiteUrl(website.website_url);
    setPageContent(website.scraped_content || website.page_content || '');
    setShowViewWebsiteModal(true);
  };

  const handleImportFAQs = async (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target.result;
        let faqsToImport = [];

        if (file.name.endsWith('.json')) {
          faqsToImport = JSON.parse(content);
        } else if (file.name.endsWith('.csv')) {
          const lines = content.split('\n');
          faqsToImport = lines.slice(1).map(line => {
            const values = line.split(',');
            return {
              question: values[0]?.trim() || '',
              answer: values[1]?.trim() || ''
            };
          }).filter(faq => faq.question && faq.answer);
        }

        if (faqsToImport.length === 0) {
          toast.error('No valid FAQs found in file');
          return;
        }

        await apiClient.post(`/api/chatbot/${chatbotId}/training/faqs/import`, {
          faqs: faqsToImport
        });
        toast.success(`${faqsToImport.length} FAQs imported successfully`);
        fetchTrainingData();
      } catch (error) {
        toast.error('Failed to import FAQs: ' + (error.response?.data?.error || error.message));
      }
    };
    reader.readAsText(file);
  };

  const handleDeleteFAQ = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      await apiClient.delete(`/api/chatbot/${chatbotId}/training/faqs/${id}`);
      toast.success('FAQ deleted successfully');
      fetchTrainingData();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const handleToggleFAQ = async (id, isEnabled) => {
    try {
      await apiClient.put(`/api/chatbot/${chatbotId}/training/faqs/${id}`, {
        is_enabled: !isEnabled
      });
      fetchTrainingData();
    } catch (error) {
      toast.error('Failed to update FAQ');
    }
  };

  // Website section handlers
  const handleAddWebsite = async () => {
    if (!websiteUrl.trim()) {
      toast.error('Please enter a website URL');
      return;
    }

    try {
      await apiClient.post(`/api/chatbot/${chatbotId}/training/websites`, {
        website_url: websiteUrl,
        is_enabled: true
      });
      toast.success('Website added successfully. Processing will start shortly.');
      setWebsiteUrl('');
      setShowWebsiteModal(false);
      fetchTrainingData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add website');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteWebsite = async (id) => {
    if (!window.confirm('Are you sure you want to delete this website?')) return;

    try {
      await apiClient.delete(`/api/chatbot/${chatbotId}/training/websites/${id}`);
      toast.success('Website deleted successfully');
      fetchTrainingData();
    } catch (error) {
      toast.error('Failed to delete website');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleToggleWebsite = async (id, isEnabled) => {
    try {
      await apiClient.put(`/api/chatbot/${chatbotId}/training/websites/${id}`, {
        is_enabled: !isEnabled
      });
      fetchTrainingData();
    } catch (error) {
      toast.error('Failed to update website');
    }
  };

  const filteredFAQs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // eslint-disable-next-line no-unused-vars
  const filteredWebsites = websites.filter(website =>
    website.website_url.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (website.page_title && website.page_title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveSection('text')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeSection === 'text'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <FileText className="mr-2 h-4 w-4" />
            Text
          </button>
          <button
            onClick={() => setActiveSection('faq')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeSection === 'faq'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            FAQs
          </button>
          {/* Website tab is currently disabled/hidden */}
          {/* <button
            onClick={() => setActiveSection('website')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeSection === 'website'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <Globe className="mr-2 h-4 w-4" />
            Website
          </button> */}
        </nav>
      </div>

      {/* Text Section */}
      {activeSection === 'text' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-medium text-gray-900">Text Training Data</h4>
              <p className="text-sm text-gray-600">
                Enter any text below, and Apollo will use it to enhance its knowledge and improve its responses.
              </p>
            </div>
            <button
              onClick={() => setShowTextModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Text
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : texts.length === 0 ? (
            <div className="text-center py-12 border border-gray-200 rounded-lg">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No text training data yet. Add text to train your AI.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {texts.map((text) => (
                <div key={text.id} className="border border-gray-200 rounded-lg p-4 flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{text.content.substring(0, 200)}{text.content.length > 200 ? '...' : ''}</p>
                    <p className="text-xs text-gray-500 mt-2">Added {new Date(text.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleText(text.id, text.is_enabled)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {text.is_enabled ? (
                        <ToggleRight className="h-5 w-5 text-green-600" />
                      ) : (
                        <ToggleLeft className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteText(text.id)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAQ Section */}
      {activeSection === 'faq' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-medium text-gray-900">FAQ Training Data</h4>
              <p className="text-sm text-gray-600">
                Add Q&As to help Apollo answer customer questions faster and more accurately.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <label className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                <Upload className="mr-2 h-4 w-4" />
                Import FAQs
                <input
                  type="file"
                  accept=".json,.csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleImportFAQs(file);
                  }}
                />
              </label>
              <button
                onClick={() => setShowFAQModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add FAQ
              </button>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredFAQs.length === 0 ? (
            <div className="text-center py-12 border border-gray-200 rounded-lg">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No FAQs yet. Add FAQs to train your AI.</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Answer</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFAQs.map((faq) => (
                    <tr key={faq.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                        {faq.question}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">
                        {faq.answer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewFAQ(faq)}
                            className="text-blue-400 hover:text-blue-600"
                            title="View FAQ"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditFAQ(faq)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Edit FAQ"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleFAQ(faq.id, faq.is_enabled)}
                            className="text-gray-400 hover:text-gray-600"
                            title={faq.is_enabled ? 'Disable' : 'Enable'}
                          >
                            {faq.is_enabled ? (
                              <ToggleRight className="h-5 w-5 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteFAQ(faq.id)}
                            className="text-red-400 hover:text-red-600"
                            title="Delete FAQ"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Website Section - Currently disabled/hidden */}

      {/* Text Modal */}
      {showTextModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-medium text-gray-900">Add Text</h3>
              <button onClick={() => setShowTextModal(false)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Text Content</label>
                  <textarea
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={textContent}
                    onChange={(e) => setTextContent(e.target.value)}
                    placeholder="Enter text content for training..."
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => setShowTextModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddText}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Modal */}
      {showFAQModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-medium text-gray-900">{editingFAQ ? 'Edit FAQ' : 'Add FAQ'}</h3>
              <button onClick={() => {
                setShowFAQModal(false);
                setEditingFAQ(null);
                setFaqData({ question: '', answer: '' });
              }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={faqData.question}
                    onChange={(e) => setFaqData({ ...faqData, question: e.target.value })}
                    placeholder="Enter question..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={faqData.answer}
                    onChange={(e) => setFaqData({ ...faqData, answer: e.target.value })}
                    placeholder="Enter answer..."
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowFAQModal(false);
                      setEditingFAQ(null);
                      setFaqData({ question: '', answer: '' });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddFAQ}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {editingFAQ ? 'Update' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Website Modal */}
      {showWebsiteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-medium text-gray-900">Add Website</h3>
              <button onClick={() => {
                setShowWebsiteModal(false);
                setWebsiteUrl('');
              }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
                  <input
                    type="url"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://www.example.com"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowWebsiteModal(false);
                      setWebsiteUrl('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddWebsite}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Add Website
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View FAQ Modal */}
      {showViewFAQModal && viewingFAQ && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-medium text-gray-900">View FAQ</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleEditFAQ(viewingFAQ)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  <Edit className="h-4 w-4 inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    setShowViewFAQModal(false);
                    setViewingFAQ(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
                    {viewingFAQ.question}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900 whitespace-pre-wrap">
                    {viewingFAQ.answer}
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>Enabled: <span className={`font-medium ${viewingFAQ.is_enabled ? 'text-green-600' : 'text-gray-400'}`}>{viewingFAQ.is_enabled ? 'Yes' : 'No'}</span></span>
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowViewFAQModal(false);
                      setViewingFAQ(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View/Edit Website Modal */}
      {showViewWebsiteModal && viewingWebsite && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-medium text-gray-900">View Website</h3>
              <button
                onClick={() => {
                  setShowViewWebsiteModal(false);
                  setViewingWebsite(null);
                  setWebsiteUrl('');
                  setPageContent('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
                  <input
                    type="url"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://www.example.com"
                  />
                </div>

                {viewingWebsite.page_title && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Page Title</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
                      {viewingWebsite.page_title}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm">
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${viewingWebsite.status === 'processed' ? 'bg-green-100 text-green-800' :
                        viewingWebsite.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                          viewingWebsite.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                        }`}>
                        {viewingWebsite.status?.toUpperCase() || 'PENDING'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pages Scraped</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
                      {viewingWebsite.pages_scraped || 0} / {viewingWebsite.total_pages || 0}
                      {viewingWebsite.progress_percentage && (
                        <span className="ml-2 text-gray-500">({viewingWebsite.progress_percentage}%)</span>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Content {pageContent && (
                      <span className="text-gray-500 font-normal">
                        ({pageContent.length.toLocaleString()} characters)
                      </span>
                    )}
                  </label>
                  <textarea
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                    value={pageContent}
                    onChange={(e) => setPageContent(e.target.value)}
                    placeholder="Page content will appear here after scraping, or you can edit it manually..."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    You can edit the scraped content manually. Changes will be saved when you click "Save Changes".
                  </p>
                </div>

                {viewingWebsite.error_message && (
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-2">Error Message</label>
                    <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-900">
                      {viewingWebsite.error_message}
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>Enabled: <span className={`font-medium ${viewingWebsite.is_enabled ? 'text-green-600' : 'text-gray-400'}`}>{viewingWebsite.is_enabled ? 'Yes' : 'No'}</span></span>
                  {viewingWebsite.last_scraped_at && (
                    <span>Last Scraped: <span className="font-medium">{new Date(viewingWebsite.last_scraped_at).toLocaleString()}</span></span>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowViewWebsiteModal(false);
                      setViewingWebsite(null);
                      setWebsiteUrl('');
                      setPageContent('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleUpdateWebsite}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotAITrainer;